import { useEffect, useRef, type ReactNode } from "react";
import {
  ArrowRight,
  BookOpen,
  Check,
  ChevronDown,
  CircleCheck,
  DatabaseZap,
  FileSpreadsheet,
  FileText,
  LoaderCircle,
  Paperclip,
  Presentation,
  Send,
  Video
} from "lucide-react";
import { AgentMark, Identicon } from "./AgentMark.tsx";
import { cn } from "@/lib/cn.ts";
import type {
  DemoTarget,
  ReplicaAgent,
  ReplicaFile,
  ReplicaMessage,
  ReplicaPerson,
  ReplicaState,
  TraceStep,
  WorkCard
} from "@/product-stage/demo/types.ts";

function agentById(state: ReplicaState, id: string): ReplicaAgent {
  const agent = state.agents.find((item) => item.id === id);
  if (!agent) throw new Error(`Unknown agent ${id}`);
  return agent;
}

function personById(state: ReplicaState, id: string): ReplicaPerson {
  const person = state.people.find((item) => item.id === id);
  if (!person) throw new Error(`Unknown person ${id}`);
  return person;
}

function entering(message: ReplicaMessage, elapsed: number): boolean {
  return elapsed - message.appearAt < 420 && elapsed >= message.appearAt;
}

export function MessagePane({
  state,
  elapsed,
  onSelect
}: {
  state: ReplicaState;
  elapsed: number;
  onSelect(target: DemoTarget): void;
}) {
  const eventsRef = useRef<HTMLDivElement>(null);
  const knowledge = state.agents.find((agent) => agent.id === "knowledge");
  const lastMessage = state.messages.at(-1);

  useEffect(() => {
    const events = eventsRef.current;
    if (!events) return;
    const frame = requestAnimationFrame(() => {
      events.scrollTop = events.scrollHeight;
    });
    return () => cancelAnimationFrame(frame);
  }, [lastMessage?.id, state.messages.length]);

  return (
    <div className="agent-thread-panel" data-view={state.view}>
      {state.view === "thread" && knowledge ? (
        <div className="agent-thread-context">
          <AgentMark agent={knowledge} variant="context" />
          <div>
            <strong>{state.space.threadTitle}</strong>
            <small>{state.space.threadMeta}</small>
          </div>
        </div>
      ) : null}

      <div ref={eventsRef} className="agent-thread-events">
        {state.messages.map((message) => (
          <MessageBlock
            key={message.id}
            message={message}
            state={state}
            fresh={entering(message, elapsed)}
            onSelect={onSelect}
          />
        ))}
      </div>

      {state.view === "direct" ? (
        <div className="composer" data-ready={state.composer.text.length > 0}>
          <div className="thread-composer-field" data-demo-target="composer-field" data-typing={state.composer.typing}>
            {state.composer.text ? <span>{state.composer.text}</span> : null}
            {!state.composer.text ? (
              <span className="composer-placeholder">{state.space.composerPlaceholder}</span>
            ) : null}
            {state.composer.typing ? <i className="caret" /> : null}
          </div>
          <div className="composer-toolbar">
            <span className="thread-composer-icon" aria-hidden>
              <Paperclip />
            </span>
            <span className="composer-send" data-demo-target="composer-send" aria-hidden>
              <Send />
            </span>
          </div>
        </div>
      ) : (
        <div className="agent-thread-composer" data-kind="reply" data-ready={state.composer.text.length > 0}>
          <div className="thread-composer-field" data-demo-target="composer-field" data-typing={state.composer.typing}>
            {state.composer.text ? <span>{state.composer.text}</span> : null}
            {!state.composer.text ? (
              <span className="composer-placeholder">{state.space.composerPlaceholder}</span>
            ) : null}
            {state.composer.typing ? <i className="caret" /> : null}
          </div>
          <span className="thread-composer-icon" aria-hidden>
            <Paperclip />
          </span>
          <span className="thread-composer-icon thread-composer-send" data-demo-target="composer-send" aria-hidden>
            <Send />
          </span>
        </div>
      )}
    </div>
  );
}

function MessageBlock({
  message,
  state,
  fresh,
  onSelect
}: {
  message: ReplicaMessage;
  state: ReplicaState;
  fresh: boolean;
  onSelect(target: DemoTarget): void;
}) {
  if (message.kind === "user") {
    return (
      <article className={cn("message-row", fresh && "is-entering")} data-own="true">
        <Identicon seed={state.account.name} className="message-avatar" />
        <div className="message-row-content">
          <MessageHead name="你" time={message.time} />
          <div className="message-body">
            {message.mentionName ? <span className="mention-chip">@{message.mentionName}</span> : null}
            <span className="message-copy">{message.text}</span>
          </div>
          {message.attachments ? <FileAttachments files={message.attachments} /> : null}
        </div>
      </article>
    );
  }

  if (message.kind === "human") {
    const person = personById(state, message.personId);
    return (
      <article className={cn("message-row", fresh && "is-entering")} data-human="true">
        <Identicon seed={person.seed} className="message-avatar human-message-avatar" />
        <div className="message-row-content">
          <MessageHead name={person.name} role={person.role} time={message.time} />
          <div className="message-body">{message.text}</div>
          {message.attachments ? <FileAttachments files={message.attachments} /> : null}
        </div>
      </article>
    );
  }

  if (message.kind === "meeting-event") {
    return (
      <button
        type="button"
        className={cn("meeting-event-card", fresh && "is-entering")}
        onClick={() => onSelect({ kind: "scene", sceneId: message.sceneId })}
      >
        <span className="meeting-event-icon">
          <Video />
        </span>
        <span>
          <strong>{message.title}</strong>
          <small>{message.detail}</small>
        </span>
        <em data-status={message.status}>{message.status === "ended" ? "已结束" : "即将开始"}</em>
        <ArrowRight />
      </button>
    );
  }

  if (message.kind === "thinking") {
    const agent = agentById(state, message.agentId);
    return (
      <article className={cn("message-row agent-result", fresh && "is-entering")} data-disposition="process">
        <AgentMark agent={agent} variant="message" animated />
        <div className="message-row-content">
          <MessageHead name={agent.name} role="智能体" time={message.time} />
          <div className="agent-execution-trace" data-open="true">
            <button type="button" className="agent-execution-trigger" tabIndex={-1}>
              <LoaderCircle className="spin" aria-hidden />
              <span className="agent-execution-copy">
                <span className="agent-trace-shimmer">{message.label}</span>
                <small>
                  {message.steps.length} 个步骤 · {message.elapsed}
                </small>
              </span>
              <ChevronDown aria-hidden />
            </button>
            <ol className="agent-execution-list">
              {message.steps.map((step) => (
                <TraceStepRow key={step.id} step={step} />
              ))}
            </ol>
          </div>
        </div>
      </article>
    );
  }

  const agent = agentById(state, message.agentId);
  return (
    <article className={cn("message-row agent-result", fresh && "is-entering")} data-disposition="complete">
      <AgentMark agent={agent} variant="message" />
      <div className="message-row-content">
        <MessageHead name={agent.name} role="智能体" time={message.time} />
        <div className="agent-result-body">
          {message.paragraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          {message.list?.map((item) => (
            <p key={item}>{item}</p>
          ))}
          {message.card ? <WorkCardView card={message.card} onSelect={onSelect} /> : null}
        </div>
      </div>
    </article>
  );
}

function MessageHead({
  name,
  role,
  time
}: {
  name: string;
  role?: string;
  time: string;
}) {
  return (
    <div className="message-head">
      <strong>{name}</strong>
      {role ? <span className="message-role">{role}</span> : null}
      <time>{time}</time>
    </div>
  );
}

function TraceStepRow({ step }: { step: TraceStep }) {
  return (
    <li className="agent-execution-step" data-status={step.done ? "completed" : "started"}>
      <span className="agent-execution-step-icon">
        {step.done ? <CircleCheck /> : <LoaderCircle className="spin" />}
      </span>
      <span className="agent-execution-step-copy">
        <strong>{step.text}</strong>
      </span>
      {step.source ? (
        <small className="trace-source" data-source={step.source}>
          {step.source === "knowledge" ? "企业知识" : step.source === "file" ? "文件" : "系统"}
        </small>
      ) : null}
      <small className="agent-execution-step-status">{step.done ? "完成" : "进行中"}</small>
    </li>
  );
}

function FileAttachments({ files }: { files: readonly ReplicaFile[] }) {
  return (
    <div className="message-attachments">
      {files.map((file) => (
        <button key={file.name} type="button" className="message-file" data-kind={file.kind} tabIndex={-1}>
          <span className="message-file-icon">{fileIcon(file.kind)}</span>
          <span>
            <strong>{file.name}</strong>
            <small>{file.detail}</small>
            {file.provenance ? <em>{file.provenance}</em> : null}
          </span>
        </button>
      ))}
    </div>
  );
}

function WorkCardView({
  card,
  onSelect
}: {
  card: WorkCard;
  onSelect(target: DemoTarget): void;
}) {
  if (card.kind === "draft") {
    return (
      <div className="work-card" data-kind="draft">
        <WorkCardHeader icon={<FileText />} file={card.file} />
        <div className="work-card-stats">
          {card.stats.map((stat) => (
            <span key={stat.label}>
              <strong>{stat.value}</strong>
              <small>{stat.label}</small>
            </span>
          ))}
        </div>
        {card.changes ? (
          <ul className="work-card-list">
            {card.changes.map((change) => (
              <li key={change}>
                <Check />
                {change}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  if (card.kind === "workbook") {
    return (
      <div className="work-card" data-kind="workbook">
        <WorkCardHeader icon={<FileSpreadsheet />} file={card.file} />
        <div className="workbook-metrics">
          {card.metrics.map((metric) => (
            <span key={metric.label}>
              <small>{metric.label}</small>
              <strong>{metric.value}</strong>
            </span>
          ))}
        </div>
        <div className="workbook-changes">
          {card.changes.map((change) => (
            <code key={change}>{change}</code>
          ))}
        </div>
      </div>
    );
  }

  if (card.kind === "meeting-summary") {
    return (
      <div className="work-card" data-kind="meeting">
        <WorkCardHeader icon={<Video />} file={card.file} />
        <div className="work-card-stats">
          {card.stats.map((stat) => (
            <span key={stat.label}>
              <strong>{stat.value}</strong>
              <small>{stat.label}</small>
            </span>
          ))}
        </div>
        <ol className="meeting-card-decisions">
          {card.decisions.map((decision) => (
            <li key={decision}>
              <CircleCheck />
              {decision}
            </li>
          ))}
        </ol>
      </div>
    );
  }

  if (card.kind === "presentation") {
    return (
      <div className="work-card" data-kind="presentation">
        <WorkCardHeader icon={<Presentation />} file={card.file} />
        <div className="presentation-preview">
          {card.slides.slice(0, 4).map((slide, index) => (
            <span key={slide}>
              <i>{index + 1}</i>
              <small>{slide}</small>
            </span>
          ))}
        </div>
        <div className="presentation-sources">
          <BookOpen />
          <span>{card.sources.join(" + ")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="work-card" data-kind="crm" data-status={card.status}>
      <div className="crm-card-head">
        <span>
          <DatabaseZap />
        </span>
        <div>
          <strong>{card.system}</strong>
          <small>{card.status === "written" ? "字段校验通过 · 已写入" : "变更预览 · 尚未写入"}</small>
        </div>
        <em>{card.status === "written" ? "已写入" : "待确认"}</em>
      </div>
      <div className="crm-change-list">
        {card.changes.map((change) => (
          <div key={change.label}>
            <strong>{change.label}</strong>
            <span>{change.before}</span>
            <ArrowRight />
            <b>{change.after}</b>
          </div>
        ))}
      </div>
      <div className="crm-attachments">
        <Paperclip />
        {card.attachments.length} 个交付文件
      </div>
      <button
        type="button"
        className="crm-confirm"
        disabled={card.status === "written"}
        onClick={() => onSelect({ kind: "action", action: "approve-crm" })}
      >
        {card.status === "written" ? <CircleCheck /> : <DatabaseZap />}
        {card.status === "written" ? "已写入 CRM" : "确认写入 CRM"}
      </button>
    </div>
  );
}

function WorkCardHeader({
  icon,
  file
}: {
  icon: ReactNode;
  file: ReplicaFile;
}) {
  return (
    <div className="work-card-head">
      <span className="work-card-icon">{icon}</span>
      <span>
        <strong>{file.name}</strong>
        <small>{file.detail}</small>
        {file.provenance ? <em>{file.provenance}</em> : null}
      </span>
    </div>
  );
}

function fileIcon(kind: ReplicaFile["kind"]) {
  if (kind === "xlsx") return <FileSpreadsheet />;
  if (kind === "pptx") return <Presentation />;
  if (kind === "pdf") return <FileText />;
  return <FileText />;
}
